import React from "react";
import { Link } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import utils from "../../universal/utils";

import {
  PUBLIC_FOOTER_SECTIONS,
} from "./siteData";
import {
  DEFAULT_DISCORD_INVITE_URL,
  fetchPublicDiscordInviteUrl,
} from "./discordInviteUrl";

export default function PublicFooter() {
  const [discordInviteUrl, setDiscordInviteUrl] = React.useState(
    DEFAULT_DISCORD_INVITE_URL
  );

  React.useEffect(() => {
    let cancelled = false;

    fetchPublicDiscordInviteUrl(utils.getEdgeBasePath())
      .then((resolvedUrl) => {
        if (!cancelled) {
          setDiscordInviteUrl(resolvedUrl);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const footerSections = React.useMemo(
    () =>
      PUBLIC_FOOTER_SECTIONS.map((section) => {
        if (section.title !== "Support") {
          return section;
        }

        return {
          ...section,
          links: section.links.map((link) =>
            link.label === "Discord"
              ? { ...link, href: discordInviteUrl }
              : link
          ),
        };
      }),
    [discordInviteUrl]
  );

  return (
    <Box
      component="footer"
      sx={{
        pt: { xs: 10, md: 14 },
        background:
          "linear-gradient(180deg, #111111 0%, #0c0c0c 40%, #090909 100%)",
        color: "#ffffff",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ py: { xs: 5, md: 6 } }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ maxWidth: 360 }}>
                <Box
                  component="img"
                  alt="Ouro"
                  src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png"
                  sx={{ height: 32, display: "block", mb: 2 }}
                />
                <Typography
                  sx={{
                    fontSize: "0.96rem",
                    lineHeight: 1.7,
                    color: "rgba(255,255,255,0.68)",
                  }}
                >
                  Ouro is building a more accountable Counter-Strike experience
                  around verified players, reputation-aware trust, and stronger
                  matchmaking controls.
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "0.9rem",
                    color: "rgba(255,255,255,0.58)",
                  }}
                >
                  Support:{" "}
                  <Typography
                    component={Link}
                    to="/support"
                    sx={{
                      color: "#ffffff",
                      textDecoration: "none",
                      display: "inline",
                      fontSize: "inherit",
                    }}
                  >
                    Open a ticket
                  </Typography>
                </Typography>
                <Typography
                  sx={{
                    mt: 0.75,
                    fontSize: "0.9rem",
                    color: "rgba(255,255,255,0.58)",
                  }}
                >
                  Community:
                  {" "}
                  <Typography component="span" sx={{ display: "inline" }}>
                    <Typography
                      component="a"
                      href={discordInviteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#ffffff",
                        textDecoration: "none",
                        display: "inline",
                        fontSize: "inherit",
                      }}
                    >
                      Discord
                    </Typography>
                  </Typography>
                </Typography>
              </Box>
            </Grid>

            {footerSections.map((section) => (
              <Grid key={section.title} size={{ xs: 6, md: 2 }}>
                <Typography
                  sx={{
                    mb: 1.5,
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.5)",
                  }}
                >
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {section.links.map((link) => {
                    const commonSx = {
                      fontSize: "0.95rem",
                      color: "rgba(255,255,255,0.74)",
                      textDecoration: "none",
                      transition: "color 0.15s ease-in-out",
                      "&:hover": {
                        color: "#ffffff",
                      },
                    };

                    if (link.to) {
                      return (
                        <Typography
                          key={link.label}
                          component={Link}
                          to={link.to}
                          sx={commonSx}
                        >
                          {link.label}
                        </Typography>
                      );
                    }

                    return (
                      <Typography
                        key={link.label}
                        component="a"
                        href={link.href}
                        target={link.href.startsWith("http") ? "_blank" : undefined}
                        rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        sx={commonSx}
                      >
                        {link.label}
                      </Typography>
                    );
                  })}
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />

          <Typography
            sx={{
              fontSize: "0.86rem",
              color: "rgba(255,255,255,0.45)",
              textAlign: { xs: "left", md: "center" },
            }}
          >
            Ouro Matchmaking. Built for serious Counter-Strike players who care
            about cleaner games and stronger accountability.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
